import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { createContextualCan } from '@casl/react';
import { Ability, RawRuleOf } from '@casl/ability';
import {
  ProjectAccessAction,
  ProjectAccessSubject,
} from '../../../server/abilities/ProjectAccessEnums';

export interface ProjectAccessProviderInputs {
  ability: string;
  children: ReactNode;
}

export type ProjectAccessAbility = Ability<
  [ProjectAccessAction, ProjectAccessSubject]
>;

const unpackAbility = (ability: string) => {
  try {
    return JSON.parse(ability).A as RawRuleOf<ProjectAccessAbility>[];
  } catch (error) {
    throw new Error(
      `Error unpacking \`ProjectAccessAbility\` within \`ProjectAccessContext\` ${error}`
    );
  }
};

export const ProjectAccessContext = createContext<ProjectAccessAbility>(
  new Ability()
);
ProjectAccessContext.displayName = 'ProjectAccessContext';

export const Can = createContextualCan<ProjectAccessAbility>(
  ProjectAccessContext.Consumer
);

export function useProjectAccessAbility() {
  return useContext(ProjectAccessContext);
}

export const ProjectAccessProvider = ({
  ability,
  children,
}: ProjectAccessProviderInputs) => {
  const memoizedAbility = useMemo<ProjectAccessAbility>(() => {
    const unpackedAbility = unpackAbility(ability || JSON.stringify({ A: [] }));
    const newAbility = new Ability(unpackedAbility) as ProjectAccessAbility;
    return newAbility;
  }, [ability]);

  return (
    <ProjectAccessContext.Provider value={memoizedAbility}>
      {children}
    </ProjectAccessContext.Provider>
  );
};
